import { useEffectEvent } from '@floating-ui/react/utils';
import { useLocation } from 'react-router';

import { useActiveFormStore } from '@/blocks/active-form/useActiveFormStore';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

import { inferModuleFromPathname } from '../utils/inferModuleFromPathname';

/**
 * Page/form context sent up with an agent turn so the agent knows where the user
 * is and which form is on screen (AID-379; spec
 * `docs/ai-chat-form-prefill-spec.md` §4.2).
 *
 * This shape MUST stay in lockstep with the gateway's `pageContextSchema`
 * (`services/ai-gateway/src/domain/types/page-context.ts`, AID-378). That schema
 * is `.strict()` at every level and the socket handler parses it with
 * `.catch(undefined)` — so an extra or misnamed key does not fail loudly, it
 * silently drops the WHOLE context and the agent quietly loses form awareness.
 * Hence the explicit field-by-field mapping below rather than spreading the
 * store's descriptor straight through.
 */
export interface PageContext {
  /** Current SPA route (`location.pathname`). */
  route: string;
  /** Backend module key inferred from the route, or `null` when none maps. */
  module: string | null;
  /** The form currently on screen; absent when the user isn't on one. */
  activeForm?: {
    entityType: string;
    recordId: string | null;
    fields: {
      key: string;
      label: string;
      displayType: string;
      required: boolean;
      enumOptions?: string[];
    }[];
  };
}

/**
 * Build the page context for the NEXT agent turn.
 *
 * Returns a getter rather than a value: the active form is read as a one-shot
 * SNAPSHOT via `useActiveFormStore.getState()` at send time, deliberately NOT as
 * a reactive subscription. Subscribing would re-render the chat panel on every
 * keystroke in the form (RHF publishes on change) for context that only matters
 * at the moment a message is sent.
 *
 * The getter is also STABLE across renders (`useEffectEvent`), which matters for
 * more than tidiness. A getter that changed identity on navigation would churn
 * the deps of every consumer's `useCallback` — and the starter chips in
 * `useChatOptions` hold onto the sender they were built with, so a chip rendered
 * before a navigation would go on reporting the route the user had left. Reading
 * the latest render's `pathname` through a stable function makes the snapshot
 * honest for the route as well as the form.
 *
 * `activeForm` is present only while a form publishes to the bridge store, which
 * is opt-in per call site (`publishActiveForm`, gated on the feature flag — see
 * AID-382). With no publisher wired, every turn sends route + module only, which
 * is exactly the pre-feature behaviour plus a route hint.
 */
export const usePageContext = (): (() => PageContext) => {
  const { pathname } = useLocation();
  const formPrefillEnabled = useIsFormPrefillEnabled();

  return useEffectEvent((): PageContext => {
    // Gated here as well as at the publisher (AID-382). Belt and braces on the
    // EGRESS direction specifically: with the flag off nothing should publish,
    // so the store is empty anyway — but a future call site that passes
    // `publishActiveForm` without gating it would otherwise start sending form
    // descriptors to the model with the feature switched off, and nothing about
    // that failure is visible from the UI.
    const formState = formPrefillEnabled
      ? useActiveFormStore.getState().formState
      : null;

    return {
      route: pathname,
      module: inferModuleFromPathname(pathname),
      ...(formState
        ? {
            activeForm: {
              entityType: formState.entityType,
              recordId: formState.recordId,
              fields: formState.descriptor.map((field) => ({
                key: field.key,
                label: field.label,
                displayType: field.displayType,
                required: field.required,
                ...(field.enumOptions
                  ? { enumOptions: field.enumOptions }
                  : {}),
              })),
            },
          }
        : {}),
    };
  });
};
